import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, TemplateRef, computed, inject, input, model, output } from '@angular/core';
import { ENVIRONMENT } from '@cfp/core';
import { ReportPage } from '@cfp/features-shared/portfolio-buildings-roadmap-report';
import { EnergyLabelEpc, EnergyLabelService, isBetterThanRegistered } from '@cfp/shared/energy-label';
import { PortfolioLocationsMap } from '@cfp/shared/portfolio/locations-map';
import { Badge, Card, Co2Module, CurrencyModule, Icon, InfoList, InfoListItem, PaybackPeriodModule } from '@cfp/ui';
import { TranslateModule } from '@ngx-translate/core';
import { EnergyLabelTypeDTO, LabelGroupDTO, LloydsRoadmapPortfolioDetailsDTO } from 'csr-asset-app';
import { LbgReportBuildingsTable } from '../../components/lbg-report-buildings-table';
import {
  ADDITIONAL_PAGE_TABLE_BODY_BUDGET_PX,
  FIRST_PAGE_TABLE_BODY_BUDGET_PX,
  buildingsLegendExtraReservePx,
  paginateBuildingsByRowHeight
} from './paginate-buildings-by-row-height';

@Component({
  selector: 'cfp-lbg-report-buildings-pages',
  templateUrl: 'report-buildings-pages.html',
  imports: [
    ReportPage,
    Card,
    InfoList,
    InfoListItem,
    PortfolioLocationsMap,
    LbgReportBuildingsTable,
    TranslateModule,
    CurrencyModule,
    Co2Module,
    DecimalPipe,
    PaybackPeriodModule,
    Icon,
    Badge,
    EnergyLabelEpc
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LbgReportBuildingsPages {
  public readonly portfolioId = input.required<number>();
  public readonly customerName = input.required<string>();
  public readonly lloydsRoadmap = input.required<LloydsRoadmapPortfolioDetailsDTO>();
  public readonly headerLogo = input<string | undefined>(undefined);
  public readonly chromeFooter = input<TemplateRef<unknown> | undefined>(undefined);
  public readonly mapReadyForPrint = output<boolean>();

  /** Two-way bound so the map can enable fit-bounds once positions load (helps print/PDF pins). */
  protected readonly mapFitBoundsEnabled = model(true);

  protected readonly environment = inject(ENVIRONMENT);
  private readonly energyLabelService = inject(EnergyLabelService);

  protected readonly LabelGroupDTO = LabelGroupDTO;

  protected readonly sortedBuildings = computed(() => {
    const buildings = [...(this.lloydsRoadmap()?.buildings ?? [])];
    buildings.sort(
      (a, b) =>
        (a.energyLabels
          ? this.energyLabelService.getSortIndex(LabelGroupDTO.EPC, a.energyLabels.currentEnergyLabel.label)
          : 1000) -
        (b.energyLabels
          ? this.energyLabelService.getSortIndex(LabelGroupDTO.EPC, b.energyLabels.currentEnergyLabel.label)
          : 1000)
    );
    return buildings;
  });

  protected readonly hasPlan = computed(() => this.sortedBuildings().some((building) => building.roadmap != null));

  protected readonly hasCalculatedBetterLabels = computed(() =>
    this.sortedBuildings().some((building) => isBetterThanRegistered(building.energyLabels?.labelSituationType))
  );

  protected readonly hasGreenLoanEligible = computed(() =>
    this.sortedBuildings().some((building) => building.greenLoanEligible)
  );

  protected readonly hasExpiredLabels = computed(() => {
    const now = new Date();
    return this.sortedBuildings().some((building) => {
      const current = building.energyLabels?.currentEnergyLabel;
      return (
        current?.labelType === EnergyLabelTypeDTO.REGISTERED &&
        !!current.validUntil &&
        new Date(current.validUntil) < now
      );
    });
  });

  protected readonly hasAlmostExpiredLabels = computed(() => {
    const now = new Date();
    const inSixMonths = new Date(now);
    inSixMonths.setMonth(inSixMonths.getMonth() + 6);
    return this.sortedBuildings().some((building) => {
      const current = building.energyLabels?.currentEnergyLabel;
      if (current?.labelType !== EnergyLabelTypeDTO.REGISTERED || !current.validUntil) {
        return false;
      }
      const validUntil = new Date(current.validUntil);
      return validUntil >= now && validUntil < inSixMonths;
    });
  });

  /** Always: estimated + registered; plus conditional legend items. */
  protected readonly legendItemCount = computed(() => {
    let count = 2;
    if (this.hasCalculatedBetterLabels()) {
      count += 1;
    }
    if (this.hasExpiredLabels()) {
      count += 1;
    }
    if (this.hasAlmostExpiredLabels()) {
      count += 1;
    }
    if (this.hasPlan()) {
      count += 1;
    }
    if (this.hasGreenLoanEligible()) {
      count += 1;
    }
    return count;
  });

  protected readonly pages = computed(() => {
    const extraLegendReservePx = buildingsLegendExtraReservePx(this.legendItemCount());
    return paginateBuildingsByRowHeight(
      this.sortedBuildings(),
      FIRST_PAGE_TABLE_BODY_BUDGET_PX - extraLegendReservePx,
      ADDITIONAL_PAGE_TABLE_BODY_BUDGET_PX - extraLegendReservePx
    );
  });
}
